import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { supOrgTripDetailsFun, supStayManualBookingFun } from '../../../api/supportAdmin';
import { stayQuoteFun } from '../../../api/bookingAPI';
import errorHandle from '../../../utils/errorHandle';
import styles from './supBookingStay.module.css';

/*
 * Header "Add Booking" (AddBookingSup) > Camp: a stay booked by phone / walk-in,
 * shown under the organizer + stay pickers. The price, stay rules and free
 * rooms are checked on the server exactly like a website booking; the rooms
 * are taken as soon as it's saved.
 *   listingId: the stay picked above
 *   onSaved:   after it's saved (closes the popup, refreshes the bookings list)
 */

const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (iso, n) => {
    const d = new Date(`${iso}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
};
const blankForm = (listingId = '') => ({
    listingId,
    checkIn: plusDays(today(), 1),
    checkOut: plusDays(today(), 2),
    adults: 2,
    children: 0,
    pets: 0,
    items: [{ unitId: '', rooms: 1 }],
    name: '',
    phoneNumber: '',
    emailId: '',
    amountCollected: '',
    specialRequests: ''
});

// support side by default; the organizer side passes its own (api/orgCampAPI.js)
const SUPPORT_API = {
    units: (listingId) =>
        supOrgTripDetailsFun(listingId, 'camp').then((res) => res?.data?.units || []),
    save: (data) => supStayManualBookingFun(data)
};

export default function StayManualBooking({
    listingId = '',
    onClose,
    onSaved,
    api = SUPPORT_API,
    collectedLabel = 'Amount collected by organizer (₹)'
}) {
    const open = Boolean(listingId);
    const [form, setForm] = useState(() => blankForm(listingId));
    const [units, setUnits] = useState([]);
    const [quote, setQuote] = useState(null); // { total, nights } | { error }
    const [saving, setSaving] = useState(false);

    const set = (patch) => setForm((f) => ({ ...f, ...patch }));

    // fresh form for each stay picked
    useEffect(() => {
        setForm(blankForm(listingId));
    }, [listingId]);

    // the chosen stay's rooms / tents
    useEffect(() => {
        if (!open || !form.listingId) {
            setUnits([]);
            return;
        }
        let gone = false;
        api.units(form.listingId)
            .then((units) => {
                if (gone) return;
                const list = (units || []).filter((u) => u?.isActive !== false);
                setUnits(list);
                setForm((f) => ({ ...f, items: [{ unitId: list[0]?._id || '', rooms: 1 }] }));
            })
            .catch((error) => !gone && errorHandle(error));
        return () => {
            gone = true;
        };
    }, [open, form.listingId]);

    const body = useMemo(
        () => ({
            listingId: form.listingId,
            checkIn: form.checkIn,
            checkOut: form.checkOut,
            adults: Number(form.adults) || 0,
            children: Number(form.children) || 0,
            pets: Number(form.pets) || 0,
            items: form.items.filter((i) => i.unitId).map((i) => ({ unitId: i.unitId, rooms: Number(i.rooms) || 1 }))
        }),
        [form.listingId, form.checkIn, form.checkOut, form.adults, form.children, form.pets, form.items]
    );

    // live price (same server quote as the website)
    useEffect(() => {
        if (!open || !body.listingId || !body.items.length || !body.checkIn || !body.checkOut) {
            setQuote(null);
            return;
        }
        let gone = false;
        const t = setTimeout(() => {
            stayQuoteFun(body)
                .then((res) => !gone && setQuote({ total: res?.data?.quote?.total, nights: res?.data?.quote?.nights }))
                .catch(
                    (error) =>
                        !gone && setQuote({ error: error?.response?.data?.message || 'These rooms can’t be booked.' })
                );
        }, 400);
        return () => {
            gone = true;
            clearTimeout(t);
        };
    }, [open, body]);

    const setItem = (index, patch) =>
        set({ items: form.items.map((it, i) => (i === index ? { ...it, ...patch } : it)) });

    const save = async () => {
        if (!form.name.trim() || !/^[6-9]\d{9}$/.test(form.phoneNumber)) {
            toast.error('Please enter the guest’s name and a valid 10-digit phone number.');
            return;
        }
        setSaving(true);
        try {
            await api.save({
                ...body,
                leadGuest: { name: form.name.trim(), phoneNumber: form.phoneNumber, emailId: form.emailId.trim() },
                amountCollected: form.amountCollected === '' ? undefined : Number(form.amountCollected),
                specialRequests: form.specialRequests
            });
            toast.success('Booking added. The rooms are now taken.');
            window.dispatchEvent(new Event('sup-booking-added'));
            onSaved?.();
        } catch (error) {
            errorHandle(error);
        } finally {
            setSaving(false);
        }
    };

    if (!open) return null;

    return (
        <div className={styles.inlineForm}>
            <div className={styles.form}>
                <div className={styles.grid2}>
                    <div className={styles.field}>
                        <label htmlFor='smbIn'>Check-in</label>
                        <input
                            id='smbIn'
                            type='date'
                            value={form.checkIn}
                            min={today()}
                            onChange={(e) => {
                                const checkIn = e.target.value;
                                set({
                                    checkIn,
                                    checkOut: form.checkOut > checkIn ? form.checkOut : plusDays(checkIn, 1)
                                });
                            }}
                        />
                    </div>
                    <div className={styles.field}>
                        <label htmlFor='smbOut'>Check-out</label>
                        <input
                            id='smbOut'
                            type='date'
                            value={form.checkOut}
                            min={plusDays(form.checkIn || today(), 1)}
                            onChange={(e) => set({ checkOut: e.target.value })}
                        />
                    </div>
                </div>

                <div className={styles.field}>
                    <span className={styles.label}>Rooms / tents</span>
                    {form.items.map((item, index) => (
                        <div className={styles.unitRow} key={index}>
                            <select
                                aria-label='Room type'
                                value={item.unitId}
                                onChange={(e) => setItem(index, { unitId: e.target.value })}
                            >
                                <option value=''>Select room</option>
                                {units.map((u) => (
                                    <option key={u._id} value={u._id}>
                                        {u.name}
                                    </option>
                                ))}
                            </select>
                            <input
                                aria-label='How many'
                                type='number'
                                min={1}
                                max={20}
                                value={item.rooms}
                                onChange={(e) => setItem(index, { rooms: e.target.value })}
                            />
                            {form.items.length > 1 ? (
                                <button
                                    type='button'
                                    className={styles.removeBtn}
                                    aria-label='Remove'
                                    onClick={() => set({ items: form.items.filter((_, i) => i !== index) })}
                                >
                                    ×
                                </button>
                            ) : (
                                <span />
                            )}
                        </div>
                    ))}
                    {units.length > 1 && (
                        <button
                            type='button'
                            className={styles.addLink}
                            onClick={() => set({ items: [...form.items, { unitId: '', rooms: 1 }] })}
                        >
                            + Add another room type
                        </button>
                    )}
                </div>

                <div className={styles.grid}>
                    {[
                        ['adults', 'Adults', 1],
                        ['children', 'Children', 0],
                        ['pets', 'Pets', 0]
                    ].map(([key, label, min]) => (
                        <div className={styles.field} key={key}>
                            <label htmlFor={`smb_${key}`}>{label}</label>
                            <input
                                id={`smb_${key}`}
                                type='number'
                                min={min}
                                value={form[key]}
                                onChange={(e) => set({ [key]: e.target.value })}
                            />
                        </div>
                    ))}
                </div>

                <div className={`${styles.quote} ${quote?.error ? styles.error : ''}`}>
                    {!quote && 'Pick the stay, dates and rooms to see the price.'}
                    {quote?.error}
                    {quote?.total !== undefined && !quote?.error && (
                        <>
                            Price: <strong>₹{quote.total}</strong> for {quote.nights} night
                            {quote.nights === 1 ? '' : 's'} (incl. tax)
                        </>
                    )}
                </div>

                <div className={styles.grid}>
                    <div className={styles.field}>
                        <label htmlFor='smbName'>Guest name</label>
                        <input
                            id='smbName'
                            value={form.name}
                            onChange={(e) => set({ name: e.target.value.slice(0, 50) })}
                        />
                    </div>
                    <div className={styles.field}>
                        <label htmlFor='smbPhone'>Phone</label>
                        <input
                            id='smbPhone'
                            inputMode='numeric'
                            value={form.phoneNumber}
                            onChange={(e) => set({ phoneNumber: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                        />
                    </div>
                    <div className={styles.field}>
                        <label htmlFor='smbEmail'>Email (optional)</label>
                        <input
                            id='smbEmail'
                            type='email'
                            value={form.emailId}
                            onChange={(e) => set({ emailId: e.target.value })}
                        />
                    </div>
                </div>

                <div className={styles.grid2}>
                    <div className={styles.field}>
                        <label htmlFor='smbPaid'>{collectedLabel}</label>
                        <input
                            id='smbPaid'
                            inputMode='numeric'
                            placeholder={quote?.total ? `${quote.total} (full price)` : 'Full price'}
                            value={form.amountCollected}
                            onChange={(e) => set({ amountCollected: e.target.value.replace(/\D/g, '').slice(0, 7) })}
                        />
                    </div>
                    <div className={styles.field}>
                        <label htmlFor='smbNote'>Special requests</label>
                        <textarea
                            id='smbNote'
                            value={form.specialRequests}
                            onChange={(e) => set({ specialRequests: e.target.value.slice(0, 500) })}
                        />
                    </div>
                </div>

                <div className={styles.buttons}>
                    <button type='button' className={styles.ghostBtn} onClick={onClose} disabled={saving}>
                        Cancel
                    </button>
                    <button
                        type='button'
                        className={styles.primaryBtn}
                        onClick={save}
                        disabled={saving || !body.listingId || !body.items.length || Boolean(quote?.error)}
                    >
                        Add booking
                    </button>
                </div>
            </div>
            {saving && <div className={styles.savingVeil} aria-hidden='true' />}
        </div>
    );
}
